import { Controller } from "@hotwired/stimulus";
import * as bootstrap from "bootstrap";

const STEP_LABELS = ["Entity Info", "Filing Details", "Review"];
const FILING_LABELS = {
  "1120": "1120 (C Corp)", "1120S": "1120S (S Corp)", "1065": "1065 (Partnership)",
  "1040": "1040 (Individual)", "990": "990 (Non-Profit)", "schedC": "Sch. C (Sole Prop)",
  "planning": "Tax Planning", "other": "Other",
};

export default class extends Controller {
  static targets = [
    "headerTitle", "headerSub", "progressSeg", "progressBar", "step",
    "clientName", "entityName",
    "filingBtn", "filingError",
    "states", "statesWrap", "shareholders", "foreignOwners", "taxYears", "taxYearsWrap",
    "reviewBody", "confirmation", "confirmName",
    "footer", "backBtn", "continueBtn", "submitBtn", "stepsRemaining",
    "validatable",
  ];

  static outlets = ["tax-center--marketing"];
  static values = { submitUrl: String, prefilledFiling: { type: String, default: "" } };

  connect() {
    this.currentStep = 1;
    this.selectedFiling = null;
    this.bsModal = new bootstrap.Modal(this.element);

    // Reset wizard when modal is fully hidden
    this.element.addEventListener("hidden.bs.modal", () => this.reset());
  }

  disconnect() {
    this.bsModal?.dispose();
  }

  open() {
    this.reset();
    this.bsModal.show();
  }

  close() {
    this.bsModal.hide();
  }

  reset() {
    this.currentStep = 1;
    this.selectedFiling = this.prefilledFilingValue || null;
    this.clearErrors();
    this.filingBtnTargets.forEach((b) => {
      b.classList.toggle("active", b.dataset.filingType === this.selectedFiling);
    });
    this.render();
  }

  selectFiling(event) {
    const btn = event.currentTarget;
    this.selectedFiling = btn.dataset.filingType;

    this.filingBtnTargets.forEach((b) => {
      b.classList.toggle("active", b.dataset.filingType === this.selectedFiling);
    });
    this.filingErrorTarget.classList.add("d-none");
  }

  next() {
    if (!this.validate()) return;

    if (this.currentStep === 3) {
      this.submit();
      return;
    }

    this.currentStep++;
    this.render();
  }

  back() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.render();
    }
  }

  // ── Validation ──

  validate() {
    this.clearErrors();
    let valid = true;

    if (this.currentStep === 1) {
      if (!this.clientNameTarget.value.trim()) { this.showFieldError(this.clientNameTarget); valid = false; }
      if (!this.entityNameTarget.value.trim()) { this.showFieldError(this.entityNameTarget); valid = false; }
    }

    if (this.currentStep === 2) {
      if (!this.selectedFiling) { this.filingErrorTarget.classList.remove("d-none"); valid = false; }
      if (this.selectedValues(this.statesTarget).length === 0) { this.showSelectError(this.statesWrapTarget); valid = false; }
      if (!this.shareholdersTarget.value.trim()) { this.showFieldError(this.shareholdersTarget); valid = false; }
      if (this.selectedValues(this.taxYearsTarget).length === 0) { this.showSelectError(this.taxYearsWrapTarget); valid = false; }
    }

    return valid;
  }

  showFieldError(input) {
    input.classList.add("is-invalid");
  }

  showSelectError(wrapTarget) {
    wrapTarget.classList.add("is-invalid");
  }

  clearErrors() {
    this.validatableTargets.forEach((el) => el.classList.remove("is-invalid"));
    if (this.hasFilingErrorTarget) this.filingErrorTarget.classList.add("d-none");
  }

  // ── Review ──

  buildReview() {
    const rows = [
      ["Client Name", this.clientNameTarget.value],
      ["Entity Legal Name", this.entityNameTarget.value],
      ["Filing Type", FILING_LABELS[this.selectedFiling] || this.selectedFiling],
      ["States", this.selectedValues(this.statesTarget).join(", ")],
      ["Shareholders", this.shareholdersTarget.value],
      ["Foreign Owners", this.foreignOwnersTarget.value || "None"],
      ["Tax Years", this.selectedValues(this.taxYearsTarget).sort().reverse().join(", ")],
    ];

    this.reviewBodyTarget.innerHTML = rows
      .map(([label, value]) =>
        `<div class="tc-quote-wizard-review-row">
          <span class="tc-quote-wizard-review-label">${label}</span>
          <span class="tc-quote-wizard-review-value">${this.escapeHtml(value)}</span>
        </div>`
      )
      .join("");
  }

  // ── Submit ──

  async submit() {
    this.submitBtnTarget.disabled = true;
    this.submitBtnTarget.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Submitting…';

    const body = {
      quote_request: {
        client_name: this.clientNameTarget.value.trim(),
        entity_legal_name: this.entityNameTarget.value.trim(),
        filing_type: this.selectedFiling,
        states: this.selectedValues(this.statesTarget),
        shareholders: parseInt(this.shareholdersTarget.value, 10),
        foreign_owners: this.foreignOwnersTarget.value ? parseInt(this.foreignOwnersTarget.value, 10) : null,
        tax_years: this.selectedValues(this.taxYearsTarget).map(Number),
      },
    };

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.errors?.join(", ") || "Something went wrong");
      }

      this.showConfirmation();
    } catch (error) {
      this.submitBtnTarget.disabled = false;
      this.submitBtnTarget.innerHTML = "Submit Quote Request";
      alert(error.message);
    }
  }

  showConfirmation() {
    this.stepTargets.forEach((el) => el.classList.add("d-none"));
    this.confirmationTarget.classList.remove("d-none");
    this.footerTarget.classList.add("d-none");
    this.progressBarTarget.classList.add("d-none");
    this.headerTitleTarget.textContent = "Quote Submitted!";
    this.headerSubTarget.textContent = "";
    this.confirmNameTarget.textContent = this.clientNameTarget.value;

    if (this.hasTaxCenterMarketingOutlet) {
      this.taxCenterMarketingOutlet.handleUploaded();
    }
  }

  // ── Rendering ──

  render() {
    this.buildReview();
    this.stepTargets.forEach((el) => {
      el.classList.toggle("d-none", parseInt(el.dataset.step) !== this.currentStep);
    });
    this.confirmationTarget.classList.add("d-none");
    this.footerTarget.classList.remove("d-none");
    this.progressBarTarget.classList.remove("d-none");

    this.progressSegTargets.forEach((seg, i) => {
      seg.classList.toggle("active", i < this.currentStep);
    });

    this.headerTitleTarget.textContent = "Get a Tax Quote";
    this.headerSubTarget.textContent = `Step ${this.currentStep} of 3 — ${STEP_LABELS[this.currentStep - 1]}`;

    this.backBtnTarget.classList.toggle("d-none", this.currentStep === 1);

    const remaining = 3 - this.currentStep;
    this.stepsRemainingTarget.textContent =
      remaining > 0 ? `${remaining} step${remaining > 1 ? "s" : ""} remaining` : "";

    this.continueBtnTarget.classList.toggle("d-none", this.currentStep === 3);
    this.submitBtnTarget.classList.toggle("d-none", this.currentStep !== 3);
    this.continueBtnTarget.disabled = false;
    this.submitBtnTarget.disabled = false;
  }

  // ── Helpers ──

  selectedValues(select) {
    return Array.from(select.selectedOptions).map((o) => o.value);
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }
}
